import type { ReactNode } from 'react';
import { Box } from '@mui/material';

/** Side inset the plate bleeds outward by; consumers pad by it to keep the
 *  plate inside their box. */
export const PLATE_BLEED = 6;

const FEATHER_BLUR = 6;
const FEATHER_SPREAD = 4;
/** How far the plate's feather reaches past its box; a scroll container that
 *  holds plated text pads by at least this much on the sides it would clip. */
export const PLATE_FEATHER = FEATHER_BLUR + FEATHER_SPREAD;

const TRUNCATED_TEXT_SX = {
  display: 'block',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
} as const;

const TEXT_ABOVE_PLATES_SX = { position: 'relative' } as const;

type OnBackgroundTextProps = {
  children: ReactNode;
  /** Paragraph shape: a 6px radius and a 1.7 line height. */
  multiline?: boolean;
  /** Truncate with an ellipsis inside the plate (single-line variant). The
   *  caller must also let the plate's own box shrink, e.g. `minWidth: 0` on a
   *  flex-item ancestor. */
  noWrap?: boolean;
};

/** Text placed directly on the patterned page background. Renders an opaque
 *  plate in the page colour with a soft feather around it, so the glyphs sit
 *  on flat colour (contrast is measured against the pixels behind the text).
 *  Titles, labels and date headers by default.
 *
 *  Without `noWrap` the plate is cloned per wrapped line, so it follows the
 *  text instead of filling the rectangle around it. The text sits in a
 *  positioned span so every line paints above every line's plate.
 *
 *  The feather is an outer shadow: an ancestor with `overflow: hidden` or
 *  `noWrap` clips it, so use the `noWrap` prop instead of truncating higher
 *  up. It caps the plate at the available width and truncates on a block
 *  inside it, because `text-overflow` applies to block containers and the
 *  `noWrap` plate itself is `inline-flex`. */
export const OnBackgroundText = ({
  children,
  multiline = false,
  noWrap = false,
}: OnBackgroundTextProps) => {
  const truncate = noWrap && !multiline;

  return (
    <Box
      component="span"
      sx={theme => ({
        backgroundColor: theme.palette.background.default,
        boxShadow: `0 0 ${String(FEATHER_BLUR)}px ${String(FEATHER_SPREAD)}px ${theme.palette.background.default}`,
        ...(truncate
          ? {
              display: 'inline-flex',
              alignItems: 'center',
              width: 'fit-content',
              maxWidth: `calc(100% + ${String(PLATE_BLEED * 2)}px)`,
            }
          : { display: 'inline', boxDecorationBreak: 'clone' }),
        ...(multiline
          ? {
              borderRadius: '6px',
              padding: `1px ${String(PLATE_BLEED)}px`,
              marginLeft: `-${String(PLATE_BLEED)}px`,
              lineHeight: 1.7,
            }
          : {
              borderRadius: '999px',
              padding: `2px ${String(PLATE_BLEED)}px`,
              margin: `0 -${String(PLATE_BLEED)}px`,
            }),
      })}
    >
      <Box
        component="span"
        sx={truncate ? TRUNCATED_TEXT_SX : TEXT_ABOVE_PLATES_SX}
      >
        {children}
      </Box>
    </Box>
  );
};
